import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import Intercom, {
    shutdown as intercomShutdown,
    update as intercomUpdate,
} from "@intercom/messenger-js-sdk";
import { EMPTY, Observable, Subscription, combineLatest, throwError, timer } from "rxjs";
import { catchError, concatMap, distinctUntilChanged, filter, map, retryWhen } from "rxjs/operators";
import { FeatureFlagConfig } from "@models/feature-flags/FeatureFlagConfig";
import { Me } from "@models/Me";
import { ResponseDTO } from "@models/ResponseDTO";
import { ContextService } from "@services/context.service";
import { FeatureFlagsService } from "@services/feature-flags/feature-flags.service";
import { environment } from "src/environments/environment";
import { AuthService } from "../../auth/auth.service";

const REFRESH_SKEW_MS = 5 * 60 * 1000;
const FALLBACK_LIFETIME_MS = 2 * 60 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 60 * 1000;
const MIN_FETCH_INTERVAL_MS = 60 * 1000;
const TOKEN_RETRY_COUNT = 2;
const TOKEN_RETRY_DELAY_MS = 2000;

@Injectable({ providedIn: "root" })
export class IntercomService implements OnDestroy {
    private readonly jwtHelper = new JwtHelperService();
    private sessionSubscription: Subscription;
    private tokenSubscription: Subscription;
    private refreshTimeout: string | number | NodeJS.Timeout;
    private tokenExpiresAt: number = null;
    private lastFetchAt = 0;
    private isBooted = false;
    private isBooting = false;

    constructor(
        private readonly http: HttpClient,
        private readonly auth: AuthService,
        private readonly context: ContextService,
        private readonly featureFlagsService: FeatureFlagsService
    ) {}

    init() {
        this.sessionSubscription = combineLatest([
            this.auth.isLoggedIn$.pipe(distinctUntilChanged()),
            this.context.me$,
        ]).subscribe(([isLoggedIn, me]) => {
            if (isLoggedIn && me) {
                this.boot(me);
            } else {
                this.shutdown();
            }
        });
    }

    refreshIfExpiring() {
        if (!this.isBooted || this.tokenExpiresAt === null) {
            return;
        }

        if (this.tokenExpiresAt - Date.now() > REFRESH_SKEW_MS) {
            return;
        }

        if (Date.now() - this.lastFetchAt < MIN_FETCH_INTERVAL_MS) {
            return;
        }

        this.refreshToken();
    }

    ngOnDestroy() {
        this.cancel(this.sessionSubscription);
        this.shutdown();
    }

    private boot(me: Me) {
        if (this.isBooted || this.isBooting) {
            return;
        }

        if (!this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagConfig.ENABLE_INTERCOM)) {
            return;
        }

        if (this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagConfig.SECURE_INTERCOM_REQUESTS)) {
            this.bootWithJwt(me);
            return;
        }

        Intercom({
            app_id: environment.intercom.appId,
            region: environment.intercom.region,
            email: me.email,
            name: `${me.firstName} ${me.lastName}`,
            company: {
                id: me.company?.id,
                name: me.company?.name,
            },
        });
        this.isBooted = true;
    }

    private bootWithJwt(me: Me) {
        this.isBooting = true;
        this.fetchToken(
            (token) => {
                Intercom({
                    app_id: environment.intercom.appId,
                    region: environment.intercom.region,
                    name: `${me.firstName} ${me.lastName}`,
                    intercom_user_jwt: token,
                });
                this.isBooting = false;
                this.isBooted = true;
                this.scheduleRefresh(token);
            },
            () => {
                this.isBooting = false;
            }
        );
    }

    private refreshToken() {
        this.fetchToken((token) => {
            if (!this.isBooted) {
                return;
            }

            intercomUpdate({ intercom_user_jwt: token });
            this.scheduleRefresh(token);
        });
    }

    private fetchToken(onToken: (token: string) => void, onSettled?: () => void) {
        const accessToken = this.auth.getToken();
        if (!accessToken || this.auth.tokenExpired(accessToken)) {
            if (onSettled) {
                onSettled();
            }
            return;
        }

        this.cancel(this.tokenSubscription);
        this.lastFetchAt = Date.now();
        this.tokenSubscription = this.requestToken().subscribe(onToken, undefined, onSettled);
    }

    private requestToken(): Observable<string> {
        const url = `${environment.apiUrlPrefix}/user-management/intercom/tokens`;

        return this.http
            .post<ResponseDTO<{ token: string }>>(url, {})
            .pipe(
                map(({ data }) => data.token),
                filter((token) => !!token),
                retryWhen((errors) =>
                    errors.pipe(
                        concatMap((error: HttpErrorResponse, index) =>
                            index < TOKEN_RETRY_COUNT && (error.status === 0 || error.status >= 500)
                                ? timer(TOKEN_RETRY_DELAY_MS)
                                : throwError(error)
                        )
                    )
                ),
                catchError(() => EMPTY)
            );
    }

    private scheduleRefresh(token: string) {
        this.stopRefreshTimer();
        this.tokenExpiresAt = this.getExpiry(token);

        const delay = Math.max(
            this.tokenExpiresAt - Date.now() - REFRESH_SKEW_MS,
            MIN_REFRESH_DELAY_MS
        );
        this.refreshTimeout = setTimeout(() => this.refreshToken(), delay);
    }

    private getExpiry(token: string): number {
        try {
            const expirationDate = this.jwtHelper.getTokenExpirationDate(token);
            return expirationDate ? expirationDate.getTime() : Date.now() + FALLBACK_LIFETIME_MS;
        } catch (e) {
            return Date.now() + FALLBACK_LIFETIME_MS;
        }
    }

    private shutdown() {
        if (!this.isBooted && !this.isBooting) {
            return;
        }

        this.cancel(this.tokenSubscription);
        this.stopRefreshTimer();
        this.tokenExpiresAt = null;
        this.lastFetchAt = 0;
        this.isBooted = false;
        this.isBooting = false;
        intercomShutdown();
    }

    private stopRefreshTimer() {
        if (this.refreshTimeout) {
            clearTimeout(Number(this.refreshTimeout));
            this.refreshTimeout = undefined;
        }
    }

    private cancel(subscription: Subscription) {
        if (subscription) {
            subscription.unsubscribe();
        }
    }
}
